import { Suspense } from 'react'
import { renderRoutes } from 'react-router-config'
import { Redirect, RouteChildrenProps } from 'react-router-dom'
import { useLastLocation } from 'react-router-last-location'

import { get, isEmpty } from 'lodash'
import queryString from 'query-string'

import {
  accessTo$,
  accessToInterventionDetails$,
  accessType$,
  careGapReportEnable$,
  carePlanEnable$,
  eventsEnable$,
  isAvailablePatientAppointments$,
  isAvailableStarMeasurements$,
  patientMeasurementReportEnable$,
  referralLicense$,
  rpmOutOfRangeReportEnable$,
  userEproms$
} from 'src/store/modules/auth'

import LazyImport from 'src/HOC/LazyImport'
import WithAuth from 'src/HOC/WithAuth'
import WithoutAuth from 'src/HOC/WithoutAuth'
import PatientRootRedirect from 'src/scenes/Patient/scenes/Search/PatientRootRedirect'
import { store } from 'src/store'
import { UUID_REGEXP } from 'src/utils/constants'

const getPatientUuid = (props: any) => {
  // FIXME
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const lastLocation = useLastLocation()
  const pathname = get(lastLocation, ['pathname'], '')
  const lastLocationSearch = queryString.parse(
    get(lastLocation, ['search'], '')
  )
  let patientUuid = get(lastLocationSearch, ['patient'], null)

  if (isEmpty(patientUuid)) {
    const propsSearch = queryString.parse(
      get(props, ['location', 'search'], '')
    )
    patientUuid = get(propsSearch, ['patient'], null)
  }

  return {
    pathname,
    patientUuid
  }
}

const getAccessType = () => accessType$(store.getState())
const getUserEproms = () => userEproms$(store.getState())

const getIsAvailableStarMeasurements = () =>
  isAvailableStarMeasurements$(store.getState()) &&
  carePlanEnable$(store.getState())
const getAccessToInterventionDetails = () =>
  accessToInterventionDetails$(store.getState()) &&
  carePlanEnable$(store.getState())

const OutOfRangeRpm = LazyImport(
  () => import('src/scenes/portal/reports/OutOfRangeRpm')
)

const CareGaps = LazyImport(() => import('src/scenes/portal/reports/CareGaps'))

const PatientMeasurementReport = LazyImport(
  () => import('src/scenes/portal/reports/PatientMeasurementReport')
)

const emrUser: any = [
  {
    path: '/auth',
    component: WithoutAuth(LazyImport(() => import('src/scenes/Auth'))),
    routes: [
      {
        path: '/auth',
        exact: true,
        component: () => <Redirect to="/auth/login" />
      },
      {
        path: '/auth/login',
        exact: true,
        component: LazyImport(() => import('src/scenes/Auth/scenes/Login'))
      },
      {
        path: '/auth/forgot-password',
        exact: true,
        component: LazyImport(
          () => import('src/scenes/Auth/scenes/ForgotPassword')
        )
      },
      {
        path: '/auth/set-password',
        exact: true,
        component: LazyImport(
          () => import('src/scenes/Auth/scenes/SetPassword')
        )
      },
      {
        path: '/auth/reset-password',
        exact: true,
        component: LazyImport(
          () => import('src/scenes/Auth/scenes/ResetPassword')
        )
      },
      {
        path: '*',
        component: () => <Redirect to="/auth/login" />
      }
    ]
  },
  {
    path: '/telemedicine/join-anonymously/:uuid',
    component: (props: any) => {
      const VideoConference = LazyImport(
        () => import('src/scenes/Telehealth/VideoConferenceV2')
      )

      return <VideoConference {...props} isAnonymouslyCall />
    }
  },
  {
    path: '/',
    component: WithAuth(
      LazyImport(() => import('src/components/layout/MainLayout'))
    ),
    routes: [
      {
        path: '/',
        exact: true,
        component: (p: RouteChildrenProps) => {
          const { patientUuid } = getPatientUuid(p)
          const accessType = getAccessType()

          return (
            <PatientRootRedirect
              {...p}
              patientUuid={patientUuid}
              accessType={accessType}
            />
          )
        }
      },
      {
        path: '/patient',
        component: ({ route }: { route: any }) => renderRoutes(route.routes),
        routes: [
          {
            path: `/patient/:patientUuid(${UUID_REGEXP})`,
            exact: true,
            component: LazyImport(
              () => import('src/scenes/Patient/scenes/Details')
            )
          },
          {
            path: '/patient',
            component: () => <Redirect to="/not-found" />
          }
        ]
      },
      {
        path: '/activities',
        exact: true,
        component: (props: any) => {
          const accessTo = accessTo$(store.getState())

          if (accessTo.includes('Activity Search')) {
            const Activities = LazyImport(() => import('src/scenes/Activities'))

            return <Activities {...props} />
          }

          return <Redirect to="/not-found" />
        }
      },
      {
        path: '/action-hub',
        component: (props: any) => {
          const accessTo = accessTo$(store.getState())

          if (accessTo.includes('Task Management')) {
            const Tasks = LazyImport(
              () => import('src/scenes/Tasks/scenes/Search')
            )

            return <Tasks {...props} />
          }

          return <Redirect to="/not-found" />
        }
      },
      {
        path: '/document-hub',
        component: (props: any) => {
          const accessTo = accessTo$(store.getState())

          if (accessTo.includes('Patient Document')) {
            const DocumentHub = LazyImport(
              () => import('src/scenes/Patient/scenes/DocumentHub')
            )

            return <DocumentHub {...props} />
          }

          return <Redirect to="/not-found" />
        }
      },
      {
        path: '/dashboard',
        component: ({ route }: { route: any }) => renderRoutes(route.routes),
        routes: [
          {
            path: '/dashboard/eproms',
            component: (props: any) => {
              const userEproms = getUserEproms()
              if (userEproms.length > 0) {
                const EpromsDashboard = LazyImport(
                  () => import('src/scenes/Eproms/scenes/Dashboard')
                )
                return <EpromsDashboard {...props} />
              }

              return <Redirect to="/not-found" />
            }
          },
          {
            path: '/dashboard/eproms-details',
            component: (props: any) => {
              const userEproms = getUserEproms()
              if (userEproms.length > 0) {
                const EpromsDetails = LazyImport(
                  () => import('src/scenes/Eproms/scenes/Details')
                )
                return <EpromsDetails {...props} />
              }

              return <Redirect to="/not-found" />
            }
          },
          {
            path: '/dashboard/quality-measurement',
            exact: true,
            component: (props: any) => {
              const isAvailableStarMeasurements =
                getIsAvailableStarMeasurements()

              if (isAvailableStarMeasurements) {
                const Dashboard = LazyImport(
                  () =>
                    import('src/scenes/Dashboard/QualityMeasurementDashboard')
                )

                return <Dashboard {...props} />
              }

              return <Redirect to="/not-found" />
            }
          },
          {
            path: '/dashboard/interventions',
            exact: true,
            component: (props: any) => {
              const accessToInterventionDetails =
                getAccessToInterventionDetails()

              if (accessToInterventionDetails) {
                const Details = LazyImport(
                  () => import('src/scenes/Dashboard/Interventions')
                )

                return <Details {...props} />
              }

              return <Redirect to="/not-found" />
            }
          },
          {
            path: '/dashboard/risk-mitigation',
            exact: true,
            component: (props: any) => {
              const accessTo = accessTo$(store.getState())

              if (accessTo.includes('Risk Dashboard')) {
                const RiskMitigationDashboard = LazyImport(
                  () => import('src/scenes/RiskMitigation')
                )
                return <RiskMitigationDashboard {...props} />
              }

              return <Redirect to="/not-found" />
            }
          }
        ]
      },
      {
        path: '/referrals',
        component: ({ route }: { route: any }) => renderRoutes(route.routes),
        routes: [
          {
            path: `/referrals/:referralUuid(${UUID_REGEXP})`,
            exact: true,
            component: (props: any) => {
              const referralLicense = referralLicense$(store.getState())

              const accessTo = accessTo$(store.getState())

              if (referralLicense && accessTo.includes('New Referral')) {
                const ReferralInfo = LazyImport(
                  () => import('src/scenes/Referral/scenes/ReferralInfo')
                )

                return <ReferralInfo {...props} />
              }

              return <Redirect to="/not-found" />
            }
          },
          {
            path: '/referrals',
            component: (props: any) => {
              const referralLicense = referralLicense$(store.getState())

              const accessTo = accessTo$(store.getState())

              if (referralLicense && accessTo.includes('New Referral')) {
                const ReferralSearch = LazyImport(
                  () => import('src/scenes/Referral/scenes/ReferralSearch')
                )

                return <ReferralSearch {...props} />
              }

              return <Redirect to="/not-found" />
            }
          }
        ]
      },
      {
        path: '/appointment-schedule',
        exact: true,
        component: (props: any) => {
          const isAvailablePatientAppointments =
            isAvailablePatientAppointments$(store.getState())

          if (isAvailablePatientAppointments) {
            const AppointmentSchedule = LazyImport(
              () => import('src/scenes/AppointmentSchedule')
            )
            return <AppointmentSchedule {...props} />
          }

          return <Redirect to="/not-found" />
        }
      },
      {
        path: '/reports',
        component: ({ route }: { route: any }) => renderRoutes(route.routes),
        routes: [
          {
            path: '/reports/patient-events',
            component: (props) => {
              const eventsEnable = eventsEnable$(store.getState())

              if (eventsEnable) {
                const EventReport = LazyImport(
                  () => import('src/scenes/Reports/scenes/EventReport')
                )

                return <EventReport {...props} />
              }

              return <Redirect to="/not-found" />
            }
          },
          {
            path: '/reports/activity-log',
            component: (props) => {
              const accessTo = accessTo$(store.getState())

              if (accessTo.includes('Activity Log Report')) {
                const ActivityLogReport = LazyImport(
                  () => import('src/scenes/Reports/ActivityLog')
                )

                return <ActivityLogReport {...props} />
              }

              return <Redirect to="/not-found" />
            }
          },
          {
            path: '/reports/out-of-range-rpm',
            component: (props: any) => {
              const rpmOutOfRangeReportEnable = rpmOutOfRangeReportEnable$(
                store.getState()
              )

              if (rpmOutOfRangeReportEnable) {
                return <OutOfRangeRpm {...props} />
              }

              return <Redirect to="/not-found" />
            }
          },
          {
            path: '/reports/care-gap',
            component: (props: any) => {
              const careGapReportEnable = careGapReportEnable$(store.getState())

              if (careGapReportEnable) {
                return <CareGaps {...props} />
              }

              return <Redirect to="/not-found" />
            }
          },
          {
            path: '/reports/patient-measurement-report',
            component: (props: any) => {
              const patientMeasurementReportEnable =
                patientMeasurementReportEnable$(store.getState())

              if (patientMeasurementReportEnable) {
                return <PatientMeasurementReport {...props} />
              }

              return <Redirect to="/not-found" />
            }
          }
        ]
      },
      {
        path: '/caregivers',
        exact: true,
        component: (props: any) => {
          const accessTo = accessTo$(store.getState())

          if (accessTo.includes('EMR CareGiver Manager')) {
            const Caregivers = LazyImport(() => import('src/scenes/Caregivers'))

            return <Caregivers {...props} />
          }

          return <Redirect to="/not-found" />
        }
      },
      {
        exact: true,
        path: '/actions/telemedicine/video-conference',
        component: (props: any) => {
          const VideoConference = LazyImport(
            () => import('src/scenes/Telehealth/VideoConferenceV2')
          )

          return <VideoConference {...props} />
        }
      },
      {
        path: '/not-found',
        component: LazyImport(() => import('src/scenes/NotFound'))
      },
      {
        path: '*',
        component: () => <Redirect to="/not-found" />
      }
    ]
  }
]

export default emrUser
